/**
 * Configuración global del sistema Clinical Graph Triage
 * Constantes médicas, físicas y de visualización
 */

import * as THREE from 'three';
import { TriagePriority, NodeType } from '../types/medical';

// ═══════════════════════════════════════════════════════════════════════════════
// CONFIGURACIÓN GENERAL
// ═══════════════════════════════════════════════════════════════════════════════

export const APP_CONFIG = {
  NAME: 'Clinical Graph Triage',
  VERSION: '1.0.0',
  DISCLAIMER: 'SOLO PARA DEMOSTRACIÓN EDUCATIVA - NO USAR PARA DIAGNÓSTICO REAL',
  MAX_NODES: 50,
  TARGET_FPS: 60,
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// PARÁMETROS FÍSICOS DEL GRAFO MÉDICO
// ═══════════════════════════════════════════════════════════════════════════════

export const MEDICAL_PHYSICS = {
  // Escala del mundo
  SCALE: 4.0,
  BOUNDARY_RADIUS: 12.0,
  
  // Tamaños de nodos por tipo. Subidos ~2× respecto a la versión previa
  // para facilitar hit-testing con mouse y hand tracking. Los radios
  // anteriores (0.12 para symptom) daban blancos muy chicos a distancia
  // normal de cámara: clicks fallaban y pinch no hiteaba nodo.
  NODE_RADIUS_BASE: 0.28,
  NODE_RADIUS: {
    'vital_sign': 0.48,
    'laboratory': 0.42,
    'imaging': 0.44,
    'diagnosis': 0.58,
    'treatment': 0.38,
    'symptom': 0.32,
    'physical_exam': 0.34,
    'complaint': 0.54,
    'history': 0.34,
    'allergy': 0.34,
  } as Record<NodeType, number>,

  // Centro del mundo médico (desplazado a la derecha para no solapar con cuerpo humano)
  GRAVITY_CENTER: new THREE.Vector3(5, 0, 0),
  
  // Fuerzas
  ATTRACTION_STRENGTH: 0.28,          // Fuerza entre nodos relacionados (baja para no apretar clusters)
  REPULSION_STRENGTH: 3.0,            // Repulsión fuerte → labels no se solapan
  CENTER_GRAVITY: 0.06,               // Tirón suave al centro
  SPRING_STIFFNESS: 0.07,             // Springs menos rígidos
  SPRING_DAMPING: 0.92,
  MAX_VELOCITY: 1.5,
  FRICTION: 0.94,

  // Distancias ideales (aumentadas: antes clusters cardiovasculares pisaban
  // labels vecinas — ej. Troponina, IAM y FC se encimaban al expandir una)
  IDEAL_DISTANCE: 7.5 as number,      // Nodos relacionados más separados
  MIN_DISTANCE: 3.8,                  // Colisión temprana (ajustada a los
                                      // radios mayores de 2024-10: sin esto
                                      // nodos vitales+labs se tocaban).
  MAX_DISTANCE: 12.0,

  // Dinámica temporal
  TIME_SCALE_DEFAULT: 60,             // 1 segundo real = 1 minuto paciente
  TIME_SCALE_MIN: 1,                  // Tiempo real
  TIME_SCALE_MAX: 3600,               // 1 hora por segundo
  DETERIORATION_RATE: 0.001,          // Tasa de empeora automática si no tratado
  
  // Arrastre temporal
  TEMPORAL_DRAG_BASE: 0.1,            // Arrastre base
  TEMPORAL_DRAG_FACTOR: 0.01,         // Factor de arrastre por antigüedad
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// COLORES MÉDICOS
// ═══════════════════════════════════════════════════════════════════════════════

export const TRIAGE_COLORS: Record<TriagePriority, number> = {
  'immediate': 0xDC2626,    // Rojo intenso
  'delayed': 0xF59E0B,      // Ámbar
  'minor': 0x10B981,        // Verde
  'expectant': 0x6B7280,    // Gris
  'deceased': 0x111827      // Negro
};

export const TRIAGE_COLOR_STRINGS: Record<TriagePriority, string> = {
  'immediate': '#DC2626',
  'delayed': '#F59E0B',
  'minor': '#10B981',
  'expectant': '#6B7280',
  'deceased': '#111827'
};

export const NODE_TYPE_COLORS: Record<NodeType, string> = {
  'vital_sign': '#EF4444',      // Rojo
  'laboratory': '#8B5CF6',      // Violeta
  'imaging': '#06B6D4',         // Cyan
  'diagnosis': '#F59E0B',       // Ámbar
  'treatment': '#10B981',       // Verde
  'symptom': '#EC4899',         // Rosa
  'physical_exam': '#3B82F6',   // Azul
  'complaint': '#DC2626',       // Rojo oscuro
  'history': '#6B7280',         // Gris
  'allergy': '#F97316'          // Naranja
};

export const SEVERITY_COLORS = {
  mild: 0x22C55E,
  moderate: 0xEAB308,
  severe: 0xF97316,
  'life-threatening': 0xDC2626
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// CONFIGURACIÓN VISUAL
// ═══════════════════════════════════════════════════════════════════════════════

export const VISUAL_CONFIG = {
  // Iluminación
  AMBIENT_LIGHT_INTENSITY: 0.4,
  DIRECTIONAL_LIGHT_INTENSITY: 0.8,
  POINT_LIGHT_INTENSITY: 0.5,
  
  // Glow/Pulso
  GLOW_NORMAL: 0.4,
  GLOW_CRITICAL: 1.5,
  CRITICAL_PULSE_THRESHOLD: 0.8,
  PULSE_SPEED_NORMAL: 1.0,
  PULSE_SPEED_CRITICAL: 3.0,
  
  // Anillos
  RING_SEGMENTS: 64,
  RING_SEVERITY: 0xFF0000,
  RING_CERTAINTY: 0x00FF00,
  RING_TIMECRIT: 0x0000FF,
  
  // Labels
  LABEL_FONT_SIZE: 14,
  LABEL_COLOR: '#FFFFFF',
  LABEL_BACKGROUND: 'rgba(0, 0, 0, 0.7)',
  LABEL_PADDING: 8,
  
  // Partículas
  TRAIL_LENGTH: 20,
  TRAIL_OPACITY: 0.3,
  
  // Transiciones
  TRANSITION_DURATION: 0.5, // segundos
  LERP_FACTOR: 0.1,
  
  // Fondo
  BACKGROUND_COLOR: 0x0a0a0a,
  FOG_COLOR: 0x0a0a0a,
  FOG_DENSITY: 0.02,
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// CONFIGURACIÓN DE HAND TRACKING
// ═══════════════════════════════════════════════════════════════════════════════

export const HAND_TRACKING_CONFIG = {
  // MediaPipe
  MODEL_PATH: 'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task',
  NUM_HANDS: 2,
  MIN_DETECTION_CONFIDENCE: 0.5,
  MIN_TRACKING_CONFIDENCE: 0.5,
  
  // One Euro Filter
  ONE_EURO_MIN_CUTOFF: 1.0,
  ONE_EURO_BETA: 0.0,
  ONE_EURO_D_CUTOFF: 1.0,
  
  // Interacción
  PINCH_THRESHOLD: 0.18,              // Distancia 3D para considerar pinch. Generoso a propósito:
                                      // la cámara frontal comprime Z → con threshold estricto el pinch
                                      // a veces no se registra. Falsos positivos ocasionales son
                                      // preferibles a clicks que no se disparan.
  HOVER_DISTANCE: 0.3,                // Distancia para hover
  SELECTION_HOLD_TIME: 1000,          // ms para seleccionar
  
  // Gestos
  GESTURE_COOLDOWN: 500,              // ms entre gestos
  SWIPE_VELOCITY_THRESHOLD: 0.5,      // Umbral para swipe
  
  // Mapeo a mundo 3D (debe coincidir con el espacio donde viven los nodos)
  HAND_SCALE: 12.0,
  HAND_OFFSET_X: 5,    // Centrado en el gravity center de los nodos
  HAND_OFFSET_Y: 0,
  HAND_OFFSET_Z: 0,    // Misma profundidad que los nodos
  FLIP_X: true,
  FLIP_Y: true,
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// CONFIGURACIÓN DE MODOS DE TOPOLOGÍA
// ═══════════════════════════════════════════════════════════════════════════════

export const TOPOLOGY_CONFIG = {
  ANATOMICAL: {
    centerAttraction: 0.3,
    systemSeparation: 2.0,
    layerHeight: 1.5,
  },
  FISIOPATHOLOGICAL: {
    causalArrowLength: 1.5,
    flowSpeed: 0.5,
  },
  TEMPORAL_SPIRAL: {
    spiralRadius: 3.0,
    spiralHeight: 0.5,
    spiralTurns: 2,
  },
  DIFFERENTIAL_COMPETITIVE: {
    centerCount: 4,
    attractionRadius: 4.0,
    competitionStrength: 0.4,
  },
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// TECLAS Y CONTROLES
// ═══════════════════════════════════════════════════════════════════════════════

export const KEYBOARD_CONTROLS = {
  // Modos de topología
  MODE_ANATOMICAL: '1',
  MODE_FISIOPATHOLOGICAL: '2',
  MODE_TEMPORAL: '3',
  MODE_DIFFERENTIAL: '4',
  
  // Control de tiempo
  PAUSE_TIME: ' ',
  SPEED_UP: 'ArrowUp',
  SPEED_DOWN: 'ArrowDown',
  RESET_TIME: 'r',
  
  // Vistas
  RESET_CAMERA: 'c',
  TOGGLE_LABELS: 'l',
  TOGGLE_EDGES: 'e',
  EMERGENCY_CLEAR: 'Escape',
  
  // Misc
  TOGGLE_HELP: 'h',
  SCREENSHOT: 's',
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// MENSAJES Y TEXTOS
// ═══════════════════════════════════════════════════════════════════════════════

export const UI_MESSAGES = {
  // Disclaimer
  DISCLAIMER_TITLE: '⚠️ ADVERTENCIA',
  DISCLAIMER_TEXT: 'Este sistema es SOLO PARA DEMOSTRACIÓN EDUCATIVA. No utilizar para diagnóstico real de pacientes.',
  
  // Estados
  LOADING: 'Cargando modelo de hand tracking...',
  READY: 'Sistema listo. Levante las manos para interactuar.',
  NO_HANDS: 'No se detectan manos',
  
  // Triage
  TRIAGE_IMMEDIATE: '🔴 ATENCIÓN INMEDIATA',
  TRIAGE_DELAYED: '🟡 PUEDE ESPERAR',
  TRIAGE_MINOR: '🟢 AMBULATORIO',
  TRIAGE_EXPECTANT: '⚫ EXPECTANTE',
  TRIAGE_DECEASED: '⚫ FALLECIDO',
  
  // Gestos
  GESTURE_PALPATION: '👌 Palpación: Examina nodo',
  GESTURE_TIME_DILATION: '🙌 Dilatación temporal: Separe/junte manos',
  GESTURE_EMERGENCY: '🙆 Emergencia: Ambas manos arriba',
  
  // Patrones detectados
  PATTERN_DETECTED: '🔔 Patrón clínico detectado:',
} as const;

// ═══════════════════════════════════════════════════════════════════════════════
// FUNCIONES DE UTILIDAD
// ═══════════════════════════════════════════════════════════════════════════════

export function getNodeRadius(nodeType: NodeType): number {
  return MEDICAL_PHYSICS.NODE_RADIUS[nodeType] ?? MEDICAL_PHYSICS.NODE_RADIUS_BASE;
}

export function getTriageColor(priority: TriagePriority): number {
  return TRIAGE_COLORS[priority];
}

export function getTriageColorString(priority: TriagePriority): string {
  return TRIAGE_COLOR_STRINGS[priority];
}

export function getNodeTypeColor(nodeType: NodeType): string {
  return NODE_TYPE_COLORS[nodeType];
}

export function getSeverityColor(severity?: string): number {
  if (!severity) return SEVERITY_COLORS.mild;
  return SEVERITY_COLORS[severity as keyof typeof SEVERITY_COLORS] ?? SEVERITY_COLORS.mild;
}
